/* Progressive entry motion: content is visible before, during failure and after enhancement. */
(() => {
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  const active = new Map();
  const seen = new WeakSet();
  let observer;
  let skipUntil = 0;
  let lastY = scrollY;
  let lastTime = performance.now();
  const settle = (element) => { active.get(element)?.finish(); active.delete(element); };
  const settleAll = () => { for (const element of active.keys()) settle(element); };
  const observe = (root = document) => {
    if (preference.matches) return;
    root.querySelectorAll('[data-fd-reveal]').forEach((element) => { if (!seen.has(element)) observer.observe(element); });
  };
  const start = () => {
    if (preference.matches) return;
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || seen.has(entry.target)) continue;
        const element = entry.target;
        seen.add(element);
        observer.unobserve(element);
        if (document.hidden || preference.matches || performance.now() < skipUntil) continue;
        element.dataset.fdRevealState = 'entered';
        const kind = element.dataset.fdReveal;
        const order = Number(element.dataset.fdRevealOrder || 0);
        const transform = kind === 'left' ? 'translateX(-10px)' : kind === 'right' ? 'translateX(10px)' : `translateY(${kind === 'soft' ? 6 : 10}px)`;
        const animation = element.animate([{ opacity: .72, transform }, { opacity: 1, transform: 'none' }], {
          duration: kind === 'soft' ? 320 : 420,
          delay: (kind === 'card' ? order % 3 : order) * 55,
          easing: 'cubic-bezier(.2,.65,.3,1)',
          fill: 'none',
        });
        active.set(element, animation);
        animation.finished.then(() => { if (active.get(element) === animation) active.delete(element); element.dataset.fdRevealState = 'settled'; }).catch(() => {});
      }
    }, { threshold: .08, rootMargin: '0px 0px -16px 0px' });
    observe();
  };
  preference.addEventListener('change', () => { settleAll(); observer?.disconnect(); if (!preference.matches) start(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) settleAll(); });
  document.addEventListener('focusin', (event) => { const element = event.target.closest('[data-fd-reveal]'); if (element) settle(element); });
  window.addEventListener('scroll', () => {
    const now = performance.now();
    if (Math.abs(scrollY - lastY) / Math.max(1, now - lastTime) > 2) { skipUntil = now + 180; settleAll(); }
    lastY = scrollY; lastTime = now;
  }, { passive: true });
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-fd-reveal]').forEach((element) => { observer?.unobserve(element); settle(element); });
  });
  start();
})();

/* Poster-first campaign lifecycle. No source is attached before visibility/policy eligibility. */
(() => {
  if (!('IntersectionObserver' in window)) return;
  const instances = new Map();
  const mount = (root = document) => {
    root.querySelectorAll('[data-fd-campaign]').forEach(stage => {
      const video = stage.querySelector('video');
      if (!video || instances.has(stage)) return;
      const button = stage.querySelector('button');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = matchMedia('(max-width: 767px)');
      const connection = navigator.connection;
      let visible = false, userPaused = false, failed = false, attached = false, generation = 0;
      const blocked = () => reduced.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
      const source = () => {
        const template = stage.querySelector(mobile.matches ? '[data-fd-video-mobile]' : '[data-fd-video-desktop]');
        const sources = [...(template?.content.querySelectorAll('source') || [])].sort((a,b)=>Number(a.dataset.width)-Number(b.dataset.width));
        const target = mobile.matches ? 1080 : 1920;
        return sources.find(s=>Number(s.dataset.width)>=target) || sources.at(-1);
      };
      const syncButton = () => {
        const playing = !video.paused && !video.ended;
        button.hidden = blocked() || !source() || failed;
        button.setAttribute('aria-pressed',String(playing));
        button.querySelector('[data-fd-play-label]').hidden = playing;
        button.querySelector('[data-fd-pause-label]').hidden = !playing;
      };
      const reset = () => {
        generation++; video.pause(); video.removeAttribute('src'); video.load(); attached=false;
        stage.classList.remove('is-playing'); syncButton();
      };
      const reveal = (token) => {
        if (token!==generation || !visible || document.hidden || blocked() || video.paused || video.readyState<2) return;
        stage.classList.add('is-playing'); syncButton();
      };
      const play = async () => {
        if (!visible || document.hidden || blocked() || userPaused || failed) return;
        const selected=source(); if (!selected) {syncButton();return;}
        const token=generation;
        if (!attached) {video.src=selected.getAttribute('src'); video.muted=true; attached=true;}
        try {
          await video.play();
          if(token!==generation) return;
          if(!visible || document.hidden || blocked() || userPaused){video.pause();return;}
          if(video.requestVideoFrameCallback) video.requestVideoFrameCallback(()=>reveal(token));
          else requestAnimationFrame(()=>reveal(token));
        } catch { if(token===generation){failed=true; reset();} }
      };
      const update = () => {if(blocked()){reset();return;} if(!visible || document.hidden){video.pause();syncButton();return;} play();};
      const onClick=()=>{if(!video.paused){userPaused=true;video.pause();syncButton();}else{userPaused=false;play();}};
      const onError=()=>{failed=true;reset();};
      const onResize=()=>{reset();failed=false;update();};
      const observer=new IntersectionObserver(entries=>{visible=entries[0].intersectionRatio>=.5;update();},{threshold:[0,.5,1]});
      observer.observe(stage.querySelector('.fd-home-hero__stage'));
      document.addEventListener('visibilitychange',update);reduced.addEventListener('change',update);mobile.addEventListener('change',onResize);connection?.addEventListener('change',update);
      button.addEventListener('click',onClick);video.addEventListener('error',onError);video.addEventListener('pause',syncButton);
      syncButton();
      instances.set(stage,()=>{observer.disconnect();document.removeEventListener('visibilitychange',update);reduced.removeEventListener('change',update);mobile.removeEventListener('change',onResize);connection?.removeEventListener('change',update);video.removeEventListener('error',onError);video.removeEventListener('pause',syncButton);button.removeEventListener('click',onClick);video.pause();video.removeAttribute('src');video.load();generation++;});
    });
  };
  mount();
  document.addEventListener('shopify:section:load',e=>mount(e.target));
  document.addEventListener('shopify:section:unload',e=>{for(const [stage,dispose] of instances){if(e.target.contains(stage)){dispose();instances.delete(stage);}}});
})();
